import { matchesCatalogFilter, type TCatalogFilter } from '@/domain/catalog/catalog-filters';
import { popularRanking } from '@/domain/catalog/popular-showcase';
import { normalizeText, splitTerms } from '@/domain/pos/search-normalize';
import type { IMenuOption } from '@/composition/use-menu';
import type { OrderType } from '@/enums/OrderEnums';
import type { ICategory } from '@/interfaces/ICategory';
import type { IItem } from '@/interfaces/IItem';
import {
  categoryHiddenReasons,
  itemHiddenReasons,
  resolveItemVisibility,
  type IGpthelpHiddenReason,
  type IGpthelpItemHiddenReason,
  type IGpthelpResolvedHiddenReason,
  type IGpthelpVisibilityCtx,
} from './gpthelp-admin-menu-visibility';
import {
  computeItemPrices,
  type IGpthelpItemPrices,
  type IGpthelpTaxCtx,
} from './gpthelp-admin-menu-pricing';

export const CATALOG_ITEM_LINES_MAX = 300;

export interface IGpthelpCatalogItemLine {
  id: number;
  name: string;
  finalPrice: number;
  priceBeforeDiscount?: number;
  hasOptions?: true;
  noPicture?: true;
  hourLimited?: true;
  hidden?: IGpthelpItemHiddenReason[];
  /** The owner kept it out of «Lo más pedido» (itself; its category's flag is on the category). */
  notInPopular?: true;
}

export interface IGpthelpCatalogCategoryNode {
  id: number;
  name: string;
  hidden?: IGpthelpHiddenReason[];
  hourLimited?: true;
  /** The owner kept its products out of «Lo más pedido». */
  notInPopular?: true;
  itemCount: number;
  customerVisibleItemCount?: number;
  items?: IGpthelpCatalogItemLine[];
}

export interface IGpthelpCatalogMenuNode {
  id: number | null;
  title: string | null;
  orderTypes: OrderType[];
  selectedInAdminUi?: true;
  offered: boolean;
  counts: {
    categories: number;
    items: number;
    itemsCustomerVisibleNow: number;
  };
  /**
   * «Lo más pedido» is on: the products labeled «Nº n más pedido» on this
   * menu in the selected branch, best first (n = position + 1); schedules
   * left out, like the Menú page's list.
   */
  popularBadgeItemIds?: number[];
  categories: IGpthelpCatalogCategoryNode[];
}

export interface IGpthelpCatalogCounts {
  menus: number;
  categoriesTotal: number;
  itemsTotal: number;
  itemsCustomerVisibleNow: number;
  itemsDisabled: number;
  itemsHiddenInThisBranch: number;
  itemsOutsideHoursNow: number;
  itemsWithoutCategory?: number;
}

export type IGpthelpCatalogNode =
  | { loaded: false }
  | {
      loaded: true;
      snapshotAt: string;
      hoursEnforced: boolean;
      counts: IGpthelpCatalogCounts;
      itemsOmitted?: true;
      discountsShowcase?: { enabled: true; itemCount: number };
      /**
       * The «Lo más pedido» switch is on: the ranking the Menú page lists
       * (best first), and the branch it comes from when it is not the
       * selected one.
       */
      popularShowcase?: { enabled: true; itemIds: number[]; fromBranch?: string };
      menus: IGpthelpCatalogMenuNode[];
      itemsWithoutCategory?: IGpthelpCatalogItemLine[];
    };

export interface IGpthelpBuildCatalogNodeInput {
  categories: ICategory[] | null;
  items: IItem[] | null;
  availableMenus: IMenuOption[] | null;
  selectedMenuId: number | null;
  ctx: IGpthelpVisibilityCtx;
  taxCtx: IGpthelpTaxCtx;
  showDiscounts: boolean;
  /** The «Lo más pedido» switch (`settings.showPopular`). */
  showPopular: boolean;
  /** The ranking the Menú page lists (popularBranchOf: best first, ≤ 24). */
  popularItemIds: readonly number[];
  /** Its branch when it is not the selected one (which has no ranking yet). */
  popularFromBranch: { id: number; name: string } | null;
}

export function resolveOfferedMenus(availableMenus: IMenuOption[] | null): IMenuOption[] {
  // availableMenus is null until restaurant settings load; without this fallback
  // everything would resolve menu_not_offered while menus load.
  if (Array.isArray(availableMenus) && availableMenus.length > 0) return availableMenus;
  return [{ id: null, title: 'Por defecto', types: [], index: 0 }];
}

/** Lowercase, accents and extra spaces removed: the Menú page's search text. */
export function normalizeSearchText(value: string): string {
  return normalizeText(value);
}

const hasPicture = (item: IItem): boolean =>
  typeof item.picture === 'string' && item.picture.trim().length > 0;

const isHourLimited = (
  hourRanges: IItem['hourRanges'],
  ctx: IGpthelpVisibilityCtx,
): boolean => ctx.hoursEnforced && Array.isArray(hourRanges) && hourRanges.length > 0;

function buildItemLine(params: {
  item: IItem;
  itemReasons: IGpthelpItemHiddenReason[];
  ctx: IGpthelpVisibilityCtx;
  prices: IGpthelpItemPrices;
}): IGpthelpCatalogItemLine {
  const { item, itemReasons, ctx, prices } = params;

  const line: IGpthelpCatalogItemLine = {
    id: item.id,
    name: item.name ?? '',
    finalPrice: prices.final,
  };

  if (prices.finalBeforeDiscount !== null) line.priceBeforeDiscount = prices.finalBeforeDiscount;
  if (item.hasOptions) line.hasOptions = true;
  if (!hasPicture(item)) line.noPicture = true;
  if (isHourLimited(item.hourRanges, ctx)) line.hourLimited = true;
  if (itemReasons.length > 0) line.hidden = itemReasons;
  if (item.popularHidden) line.notInPopular = true;

  return line;
}

interface IMenuBucket {
  node: IGpthelpCatalogMenuNode;
  offered: boolean;
  /** Its products with their categories, for «Lo más pedido». */
  rows: { item: IItem; category: ICategory }[];
}

export function buildCatalogNode(input: IGpthelpBuildCatalogNodeInput): IGpthelpCatalogNode {
  const {
    categories,
    items,
    selectedMenuId,
    ctx,
    taxCtx,
    showDiscounts,
    showPopular,
    popularItemIds,
    popularFromBranch,
  } = input;

  if (!Array.isArray(categories) || !Array.isArray(items)) {
    return { loaded: false };
  }

  const resolvedMenus = resolveOfferedMenus(input.availableMenus);
  const emitItemLines = items.length <= CATALOG_ITEM_LINES_MAX;

  const itemsByCategoryId = new Map<number, IItem[]>();
  for (const item of items) {
    const list = itemsByCategoryId.get(item.categoryId);
    if (list) list.push(item);
    else itemsByCategoryId.set(item.categoryId, [item]);
  }

  const buckets = new Map<number | null, IMenuBucket>();
  const orderedBuckets: IMenuBucket[] = [];

  for (const menu of resolvedMenus) {
    const menuId = menu.id ?? null;
    const node: IGpthelpCatalogMenuNode = {
      id: menuId,
      title: menu.title,
      orderTypes: menu.types,
      offered: true,
      counts: { categories: 0, items: 0, itemsCustomerVisibleNow: 0 },
      categories: [],
    };
    if (menuId === selectedMenuId) node.selectedInAdminUi = true;

    const bucket: IMenuBucket = { node, offered: true, rows: [] };
    buckets.set(menuId, bucket);
    orderedBuckets.push(bucket);
  }

  const getOrCreateBucket = (menuId: number | null): IMenuBucket => {
    const existing = buckets.get(menuId);
    if (existing) return existing;

    // Orphan menu: stale menuIds left behind (e.g. settings.multipleMenus turned off).
    const bucket: IMenuBucket = {
      node: {
        id: menuId,
        title: null,
        orderTypes: [],
        offered: false,
        counts: { categories: 0, items: 0, itemsCustomerVisibleNow: 0 },
        categories: [],
      },
      offered: false,
      rows: [],
    };
    buckets.set(menuId, bucket);
    orderedBuckets.push(bucket);
    return bucket;
  };

  const counts: IGpthelpCatalogCounts = {
    menus: 0,
    categoriesTotal: categories.length,
    itemsTotal: items.length,
    itemsCustomerVisibleNow: 0,
    itemsDisabled: 0,
    itemsHiddenInThisBranch: 0,
    itemsOutsideHoursNow: 0,
  };

  let discountsShowcaseCount = 0;
  const seenItemIds = new Set<number>();

  const sortedCategories = [...categories].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));

  for (const category of sortedCategories) {
    const bucket = getOrCreateBucket(category.menuId ?? null);
    const categoryReasons = categoryHiddenReasons(category, ctx);
    const categoryItems = [...(itemsByCategoryId.get(category.id) ?? [])].sort(
      (a, b) => (a.index ?? 0) - (b.index ?? 0),
    );

    const categoryNode: IGpthelpCatalogCategoryNode = {
      id: category.id,
      name: category.name ?? '',
      itemCount: categoryItems.length,
    };
    if (categoryReasons.length > 0) categoryNode.hidden = categoryReasons;
    if (category.popularHidden) categoryNode.notInPopular = true;
    if (isHourLimited(category.hourRanges, ctx)) categoryNode.hourLimited = true;

    let customerVisibleItemCount = 0;
    const itemLines: IGpthelpCatalogItemLine[] = [];

    for (const item of categoryItems) {
      seenItemIds.add(item.id);
      bucket.rows.push({ item, category });
      const itemReasons = itemHiddenReasons(item, ctx);
      const prices = computeItemPrices(item, taxCtx);

      if (itemReasons.includes('disabled')) counts.itemsDisabled += 1;
      if (itemReasons.includes('hidden_in_branch')) counts.itemsHiddenInThisBranch += 1;
      if (itemReasons.includes('outside_hours')) counts.itemsOutsideHoursNow += 1;

      const customerVisible =
        itemReasons.length === 0 && categoryReasons.length === 0 && bucket.offered;

      if (customerVisible) {
        customerVisibleItemCount += 1;
        counts.itemsCustomerVisibleNow += 1;
        bucket.node.counts.itemsCustomerVisibleNow += 1;

        if (prices.discounted !== null && hasPicture(item)) {
          discountsShowcaseCount += 1;
        }
      }

      if (emitItemLines) {
        itemLines.push(buildItemLine({ item, itemReasons, ctx, prices }));
      }
    }

    bucket.node.counts.categories += 1;
    bucket.node.counts.items += categoryItems.length;

    if (customerVisibleItemCount !== categoryNode.itemCount) {
      categoryNode.customerVisibleItemCount = customerVisibleItemCount;
    }
    if (emitItemLines) categoryNode.items = itemLines;

    bucket.node.categories.push(categoryNode);
  }

  // Items whose categoryId matches no live category: never drop silently.
  const orphanItemLines: IGpthelpCatalogItemLine[] = [];
  let itemsWithoutCategoryCount = 0;

  for (const item of items) {
    if (seenItemIds.has(item.id)) continue;
    itemsWithoutCategoryCount += 1;

    const itemReasons = itemHiddenReasons(item, ctx);
    if (itemReasons.includes('disabled')) counts.itemsDisabled += 1;
    if (itemReasons.includes('hidden_in_branch')) counts.itemsHiddenInThisBranch += 1;
    if (itemReasons.includes('outside_hours')) counts.itemsOutsideHoursNow += 1;

    if (emitItemLines) {
      orphanItemLines.push(
        buildItemLine({ item, itemReasons, ctx, prices: computeItemPrices(item, taxCtx) }),
      );
    }
  }

  counts.menus = orderedBuckets.length;
  if (itemsWithoutCategoryCount > 0) counts.itemsWithoutCategory = itemsWithoutCategoryCount;

  const node: IGpthelpCatalogNode = {
    loaded: true,
    snapshotAt: new Date().toISOString(),
    hoursEnforced: ctx.hoursEnforced,
    counts,
    menus: orderedBuckets.map((b) => b.node),
  };

  if (!emitItemLines) node.itemsOmitted = true;
  if (showDiscounts) {
    node.discountsShowcase = { enabled: true, itemCount: discountsShowcaseCount };
  }
  if (showPopular) {
    // The ranking minus what the owner kept out, itself or through its category (the storefront skips them too).
    const categoryById = new Map(categories.map((category) => [category.id, category]));
    const itemById = new Map(items.map((item) => [item.id, item]));
    const itemIds = popularItemIds.filter((id) => {
      const item = itemById.get(id);
      return !(item && (item.popularHidden || categoryById.get(item.categoryId)?.popularHidden));
    });
    node.popularShowcase = {
      enabled: true,
      itemIds,
      ...(popularFromBranch ? { fromBranch: popularFromBranch.name } : {}),
    };

    const branchId =
      popularFromBranch?.id ??
      (ctx.branchIdStr === null ? null : Number(ctx.branchIdStr));
    for (const bucket of orderedBuckets) {
      if (!bucket.offered) continue;
      const ranking = popularRanking({ rankedIds: popularItemIds, rows: bucket.rows, branchId });
      if (ranking.length > 0) {
        bucket.node.popularBadgeItemIds = ranking.map((entry) => entry.item.id);
      }
    }
  }
  if (emitItemLines && orphanItemLines.length > 0) {
    node.itemsWithoutCategory = orphanItemLines;
  }

  return node;
}

export type IGpthelpCatalogVisibilityFilter =
  | 'any'
  | 'customer_visible_now'
  | 'hidden_any_reason'
  | 'disabled'
  | 'hidden_in_branch'
  | 'outside_hours_now';

export type IGpthelpCatalogMenuFilter = { menuId: number } | { defaultMenu: true } | null;

/**
 * «Menu health» filters of the Menú page (plan §5.20): evaluated with the
 * page's own predicates, so the agent and the page agree on which products
 * lack a photo, a description or a price.
 */
export type IGpthelpCatalogHealthFilter =
  | 'no_photo'
  | 'no_description'
  | 'no_price'
  | 'scheduled'
  | 'discounted';

export const GPTHELP_CATALOG_HEALTH_FILTERS: readonly IGpthelpCatalogHealthFilter[] = [
  'no_photo',
  'no_description',
  'no_price',
  'scheduled',
  'discounted',
];

const HEALTH_PAGE_FILTERS: Record<IGpthelpCatalogHealthFilter, TCatalogFilter> = {
  no_photo: 'no-photo',
  no_description: 'no-description',
  no_price: 'no-price',
  scheduled: 'scheduled',
  discounted: 'discounted',
};

export interface IGpthelpCatalogSearchParams {
  query?: string | null;
  itemIds?: number[] | null;
  categoryId?: number | null;
  menuFilter?: IGpthelpCatalogMenuFilter;
  visibility?: IGpthelpCatalogVisibilityFilter;
  discountedOnly?: boolean;
  health?: IGpthelpCatalogHealthFilter | null;
  limit: number;
  offset: number;
}

export interface IGpthelpCatalogSearchData {
  categories: ICategory[];
  items: IItem[];
  availableMenus: IMenuOption[] | null;
  ctx: IGpthelpVisibilityCtx;
  taxCtx: IGpthelpTaxCtx;
}

export interface IGpthelpCatalogSearchRow {
  itemId: number;
  name: string;
  categoryId: number;
  categoryName: string | null;
  menuId: number | null;
  menuTitle: string | null;
  finalPrice: number;
  priceBeforeDiscount?: number;
  customerVisibleNow: boolean;
  hiddenReasons?: IGpthelpResolvedHiddenReason[];
  hasOptions?: true;
  noPicture?: true;
  hourLimited?: true;
}

export interface IGpthelpCatalogSearchResult {
  total: number;
  offset: number;
  limit: number;
  checkedAt: string;
  results: IGpthelpCatalogSearchRow[];
  missing?: number[];
  /** Set when a query matched nothing: how to search again (model-facing). */
  noMatch?: string;
}

/** What the model reads when its query matched no product. */
export const CATALOG_NO_MATCH_NOTE =
  "Nothing matched. Product names are in the menu's language: search again with words in that language (translate the user's words), in singular or shorter, or list the products of a category that fits.";

// 0 exact name | 1 name prefix | 2 name contains the query | 3 every term in the name
// 4 every term in name+description | 5 every term in name+description+category name
type IQueryMatchTier = 0 | 1 | 2 | 3 | 4 | 5;

interface ISearchQuery {
  /** The whole query, normalized. */
  normalized: string;
  /** Its words, normalized; never empty. */
  terms: string[];
}

/**
 * How well a product matches the query. Tiers 0–2 compare the whole query
 * with the name; 3–5 need every word somewhere, like the Menú page's search
 * (`domain/catalog/catalog-search.ts`): name, then description, then the
 * category name.
 */
const matchQueryTier = (
  item: IItem,
  categoryName: string,
  query: ISearchQuery,
): IQueryMatchTier | null => {
  const name = normalizeText(item.name ?? '');

  if (name === query.normalized) return 0;
  if (name.startsWith(query.normalized)) return 1;
  if (name.includes(query.normalized)) return 2;

  const hasEveryTerm = (haystack: string) =>
    query.terms.every((term) => haystack.includes(term));
  if (hasEveryTerm(name)) return 3;

  const withDescription = `${name} ${normalizeText(item.description ?? '')}`;
  if (hasEveryTerm(withDescription)) return 4;
  if (hasEveryTerm(`${withDescription} ${normalizeText(categoryName)}`)) return 5;

  return null;
};

/** The query as words; null when it has none. */
const searchQueryOf = (raw: string | null | undefined): ISearchQuery | null => {
  if (typeof raw !== 'string') return null;
  const terms = splitTerms(raw);
  return terms.length > 0 ? { normalized: normalizeText(raw), terms } : null;
};

const matchesVisibilityFilter = (
  filter: IGpthelpCatalogVisibilityFilter,
  hiddenReasons: IGpthelpResolvedHiddenReason[],
): boolean => {
  switch (filter) {
    case 'any':
      return true;
    case 'customer_visible_now':
      return hiddenReasons.length === 0;
    case 'hidden_any_reason':
      return hiddenReasons.length > 0;
    case 'disabled':
      return (
        hiddenReasons.includes('item_disabled') || hiddenReasons.includes('category_disabled')
      );
    case 'hidden_in_branch':
      return (
        hiddenReasons.includes('item_hidden_in_branch') ||
        hiddenReasons.includes('category_hidden_in_branch')
      );
    case 'outside_hours_now':
      return (
        hiddenReasons.includes('item_outside_hours') ||
        hiddenReasons.includes('category_outside_hours')
      );
  }
};

export function searchCatalogItems(
  params: IGpthelpCatalogSearchParams,
  data: IGpthelpCatalogSearchData,
): IGpthelpCatalogSearchResult {
  const { categories, items, ctx, taxCtx } = data;

  const resolvedMenus = resolveOfferedMenus(data.availableMenus);
  const offeredMenuIds = new Set<number | null>(resolvedMenus.map((m) => m.id ?? null));
  const menuTitleById = new Map<number | null, string>(
    resolvedMenus.map((m) => [m.id ?? null, m.title]),
  );
  // Mirrors the catalog `menus` array order: offered menus first, then orphan
  // menus in first-encounter order over categories sorted by index.
  const menuOrderById = new Map<number | null, number>(
    resolvedMenus.map((m, index) => [m.id ?? null, index]),
  );

  const categoriesById = new Map<number, ICategory>(categories.map((c) => [c.id, c]));
  const sortedCategories = [...categories].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  const categoryOrderById = new Map<number, number>(
    sortedCategories.map((c, index) => [c.id, index]),
  );

  for (const category of sortedCategories) {
    const menuKey = category.menuId ?? null;
    if (!menuOrderById.has(menuKey)) menuOrderById.set(menuKey, menuOrderById.size);
  }

  const itemIdsFilter =
    Array.isArray(params.itemIds) && params.itemIds.length > 0
      ? new Set(params.itemIds)
      : null;
  const menuFilter = params.menuFilter ?? null;
  const visibilityFilter = params.visibility ?? 'any';
  const query = searchQueryOf(params.query);

  interface IMatch {
    row: IGpthelpCatalogSearchRow;
    queryTier: IQueryMatchTier;
    menuOrder: number;
    categoryOrder: number;
    itemIndex: number;
  }

  const matches: IMatch[] = [];
  const foundIds = new Set<number>();
  // Products the query matched, before the visibility, health and discount filters.
  let queryHits = 0;

  for (const item of items) {
    if (itemIdsFilter && !itemIdsFilter.has(item.id)) continue;
    if (itemIdsFilter) foundIds.add(item.id);

    if (typeof params.categoryId === 'number' && item.categoryId !== params.categoryId) {
      continue;
    }

    const category = categoriesById.get(item.categoryId) ?? null;

    if (menuFilter !== null) {
      // Items whose category is missing never match any menu filter.
      if (category === null) continue;
      const categoryMenuId = category.menuId ?? null;
      if ('menuId' in menuFilter) {
        if (categoryMenuId !== menuFilter.menuId) continue;
      } else if (categoryMenuId !== null) {
        continue;
      }
    }

    let queryTier: IQueryMatchTier = 0;
    if (query !== null) {
      const tier = matchQueryTier(item, category?.name ?? '', query);
      if (tier === null) continue;
      queryTier = tier;
      queryHits += 1;
    }

    // None of the health filters depends on the branch.
    if (
      params.health &&
      !matchesCatalogFilter(HEALTH_PAGE_FILTERS[params.health], item, category, {
        branchId: null,
      })
    )
      continue;

    const prices = computeItemPrices(item, taxCtx);
    if (params.discountedOnly && prices.discounted === null) continue;

    const menuOffered =
      category !== null && offeredMenuIds.has(category.menuId ?? null);
    const visibility = resolveItemVisibility({ item, category, menuOffered, ctx });
    if (!matchesVisibilityFilter(visibilityFilter, visibility.hiddenReasons)) continue;

    const row: IGpthelpCatalogSearchRow = {
      itemId: item.id,
      name: item.name ?? '',
      categoryId: item.categoryId,
      categoryName: category?.name ?? null,
      menuId: category ? (category.menuId ?? null) : null,
      menuTitle: category ? (menuTitleById.get(category.menuId ?? null) ?? null) : null,
      finalPrice: prices.final,
      customerVisibleNow: visibility.customerVisibleNow,
    };
    if (prices.finalBeforeDiscount !== null) row.priceBeforeDiscount = prices.finalBeforeDiscount;
    if (visibility.hiddenReasons.length > 0) row.hiddenReasons = visibility.hiddenReasons;
    if (item.hasOptions) row.hasOptions = true;
    if (!hasPicture(item)) row.noPicture = true;
    if (isHourLimited(item.hourRanges, ctx)) row.hourLimited = true;

    matches.push({
      row,
      queryTier,
      menuOrder: category
        ? (menuOrderById.get(category.menuId ?? null) ?? resolvedMenus.length)
        : Number.MAX_SAFE_INTEGER,
      categoryOrder: category
        ? (categoryOrderById.get(category.id) ?? Number.MAX_SAFE_INTEGER)
        : Number.MAX_SAFE_INTEGER,
      itemIndex: typeof item.index === 'number' ? item.index : 0,
    });
  }

  if (query !== null) {
    matches.sort((a, b) => a.queryTier - b.queryTier || a.itemIndex - b.itemIndex);
  } else {
    matches.sort(
      (a, b) =>
        a.menuOrder - b.menuOrder ||
        a.categoryOrder - b.categoryOrder ||
        a.itemIndex - b.itemIndex,
    );
  }

  const page = matches.slice(params.offset, params.offset + params.limit);

  const result: IGpthelpCatalogSearchResult = {
    total: matches.length,
    offset: params.offset,
    limit: page.length,
    checkedAt: new Date().toISOString(),
    results: page.map((m) => m.row),
  };

  if (itemIdsFilter) {
    const missing = [...itemIdsFilter].filter((id) => !foundIds.has(id));
    if (missing.length > 0) result.missing = missing;
  }
  // Only a query that matched no product is a miss: one whose products the
  // other filters left out is not (searching again would not find them).
  if (query !== null && queryHits === 0) result.noMatch = CATALOG_NO_MATCH_NOTE;

  return result;
}
